import React, { FunctionComponent, MutableRefObject, useEffect } from 'react';

import { useIntl } from 'react-intl';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import PrimaryButton from '../../components/atoms/PrimaryButton';
import SecondaryButton from '../../components/atoms/SecondaryButton';
import { useMediaQueryContext } from '../../providers/MediaQueryProvider';
import { useSpotifyLoginTokenStrategy } from '../../services/Spotify/useSpotifyLoginTokenStrategy';
import { STATUS } from '../../services/Spotify/useSpotifyLoginTokenStrategy.types';

import { ContentSection } from './components/Section';
import Logo from './components/Logo';
import PhoneSlider from './components/PhoneSlider';
import WizardWrapper from './components/Wizard';
import CircleAnimations from './components/Animations/CircleAnimation';

type SectionFeatures = {
  sectionRef: MutableRefObject<any>;
};

const SectionFeatures: FunctionComponent<SectionFeatures> = ({
  sectionRef,
}) => {
  const { formatMessage } = useIntl();
  const { width } = useMediaQueryContext();
  const { actions, ...state } = useSpotifyLoginTokenStrategy();

  useEffect(() => {
    if (state.token) {
      sectionRef?.current?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.token]);

  switch (state.status) {
    case STATUS.ERROR:
      return (
        <ContentSection alignment="center" sectionRef={sectionRef}>
          <Box display="flex" justifyContent="center">
            {state.error}
          </Box>
        </ContentSection>
      );

    case STATUS.LOADING:
      return (
        <ContentSection alignment="center" sectionRef={sectionRef}>
          <Box display="flex" justifyContent="center">
            <CircleAnimations />
          </Box>
        </ContentSection>
      );
    case STATUS.LOGGED_IN:
    case STATUS.NO_LOGIN:
      return (
        <WizardWrapper
          artists={state.artists}
          noLogin={state.status === STATUS.NO_LOGIN}
          onLogout={actions.logout}
          sectionRef={sectionRef}
          user={state.user}
        />
      );
    default:
      return (
        <ContentSection
          sectionRef={sectionRef}
          alignment="left"
          supportContent={
            <Box mt={width < 960 ? 5 : 0}>
              <PhoneSlider onChange={() => {}} />
            </Box>
          }
        >
          <Typography variant="h3">
            {formatMessage(
              { id: 'features.title' },
              { logo: <Logo key="section.features.logo" /> }
            )}
          </Typography>
          <Box py={2}>
            <Typography color="textPrimary">
              {formatMessage({ id: 'features.text' })}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <SecondaryButton onClick={actions.noLogin} tabIndex={-1}>
              {formatMessage({ id: 'features.withoutLogin' })}
            </SecondaryButton>
            <Box mx={2} display="inline" />
            <PrimaryButton onClick={actions.login} tabIndex={0}>
              {formatMessage({ id: 'features.withLogin' })}
            </PrimaryButton>
          </Box>
        </ContentSection>
      );
  }
};

export default SectionFeatures;
